import React, {
  useState,
  useEffect,
  useMemo,
} from "react";
import {
  Smartphone,
  Building2,
  CheckCircle2,
  Circle,
  ChevronLeft,
  Loader2,
} from "lucide-react";
import Select from "react-select";
import { useNavigate, useParams } from "react-router-dom";

import { useCorridor } from "../hooks/useCorridor";
import { useMe, usePayoutBanks } from "../hooks/useMe";
import { useCountries } from "../hooks/useCountries";
import { useSupportedRecipientCountries } from "../hooks/useSupportedRecipientCountries";
import { useSupportedChannels } from "../hooks/useSupportedChannels";
import { isChannelSupported } from "../data/payoutCoverage";
import {safeApiErrorMessage} from "../utils/safeApiErrorMessage";

const CHANNEL_ICONS = {
  mobile_money: Smartphone,
  bank_transfer: Building2,
  sepa: Building2,
  fps: Building2,
  ach: Building2,
  wire: Building2,
  pix: Smartphone,
};

const SectionLabel = ({ children }) => (
  <h2 className="text-[9px] font-semibold uppercase tracking-[0.2em] text-[#0a2540]/40">
    {children}
  </h2>
);

const FieldLabel = ({ children }) => (
  <label className="block text-[10px] uppercase tracking-[0.18em] font-semibold text-[#0A4730]/70 mb-2">
    {children}
  </label>
);

const INPUT_CLASS =
  "w-full rounded-xl bg-white border border-slate-200 px-4 py-3 text-sm text-[#0A2540] placeholder:text-slate-400 focus:outline-none focus:border-[#0A4730] focus:ring-4 focus:ring-[#0A4730]/5 transition-all duration-200";

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 46,
    borderRadius: 12,
    backgroundColor: "#FFFFFF",
    border: state.isFocused
      ? "1.5px solid #0A4730"
      : "1px solid #E2E8F0",
    boxShadow: state.isFocused
      ? "0 0 0 4px rgba(10, 71, 48, 0.08)"
      : "0 1px 2px rgba(0,0,0,0.03)",
    fontFamily: "inherit",
    fontSize: 14,
    paddingLeft: 4,
    cursor: "pointer",
    "&:hover": {
      borderColor: "#0A4730",
    },
    transition: "all 0.2s ease",
  }),

  menu: (base) => ({
    ...base,
    borderRadius: 16,
    overflow: "hidden",
    zIndex: 999,
    boxShadow:
      "0 8px 30px rgba(10,71,48,0.12)",
    border: "1px solid #0A473015",
  }),

  menuList: (base) => ({
    ...base,
    padding: 6,
    maxHeight: 220,
  }),

  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "#0A4730"
      : state.isFocused
        ? "#F5F9F4"
        : "white",
    color: state.isSelected
      ? "white"
      : "#0A2540",
    borderRadius: 10,
    fontSize: 14,
    padding: "10px 12px",
    cursor: "pointer",
    transition: "background 0.1s",
  }),

  input: (base) => ({
    ...base,
    fontSize: 14,
    color: "#0A2540",
  }),

  placeholder: (base) => ({
    ...base,
    fontSize: 14,
    color: "#0A254050",
  }),

  singleValue: (base) => ({
    ...base,
    fontSize: 14,
    color: "#0A2540",
  }),

  indicatorSeparator: () => ({
    display: "none",
  }),

  dropdownIndicator: (base, state) => ({
    ...base,
    color: state.isFocused
      ? "#0A4730"
      : "#0A254040",
    transition:
      "color 0.15s, transform 0.2s",
    transform: state.selectProps.menuIsOpen
      ? "rotate(180deg)"
      : "rotate(0deg)",
    paddingRight: 12,
  }),

  noOptionsMessage: (base) => ({
    ...base,
    fontSize: 13,
    color: "#0A254060",
    padding: "12px 8px",
  }),
};

export function EditRecipientPage() {
  const navigate = useNavigate();
  const { id } = useParams();

  const corridor = useCorridor();

  const {
    countryCode,
    channelKey,
    activeChannel,
    availableChannels,
    recipientFields,
    values: corridorValues,
    errors: corridorErrors,
    initCorridor,
    setValue,
    validate,
    buildPaymentMethodPayload,
    getDialCode,
    getLocalMobileNumber,
  } = corridor;

  const {
    recipients,
    updateRecipientAsync,
    createPaymentMethodAsync,
    isLoading: isLoadingMe,
  } = useMe();

  const { countries } = useCountries();

  const [nickname, setNickname] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");

  const [mobileNumber, setMobileNumber] =
    useState("");

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] =
    useState(null);
  const [initialized, setInitialized] =
    useState(false);

  const recipient = useMemo(
    () =>
      recipients?.find(
        (r) => r.id === id
      ),
    [recipients, id]
  );

  const { countryOptions } =
    useSupportedRecipientCountries(
      corridor.countries,
      countries
    );

  const selectedCountry = useMemo(
    () =>
      countryOptions.find(
        (c) => c.value === countryCode
      ) || null,
    [countryOptions, countryCode]
  );

  const selectedCountryData = useMemo(
    () =>
      countries.find(
        (c) =>
          c.code?.toUpperCase() ===
          countryCode?.toUpperCase()
      ) || null,
    [countries, countryCode]
  );

  const recipientDialCode =
    selectedCountryData?.dialCode ||
    getDialCode() ||
    "";

  const isMobileChannel =
    activeChannel?.channel === "mobile_money" ||
    channelKey === "mobile_money";

  const isBankTransferWithBanks =
    isChannelSupported(
      countryCode,
      "bank_transfer"
    ) &&
    channelKey === "bank_transfer";

  const {
    banks,
    isLoadingBanks,
  } = usePayoutBanks(
    isBankTransferWithBanks
      ? countryCode
      : null
  );

  const channelEntries = useSupportedChannels(
    countryCode,
    availableChannels
  );

  const fullName =
    `${firstName} ${lastName}`.trim();

  const displayMobileNumber = useMemo(() => {
    if (!mobileNumber) {
      return "";
    }

    return getLocalMobileNumber(
      mobileNumber
    );
  }, [
    mobileNumber,
    getLocalMobileNumber,
  ]);

  useEffect(() => {
    if (!recipient || initialized) {
      return;
    }

    const recipientCountry =
      String(
        recipient.country_code || ""
      )
        .trim()
        .toUpperCase();

    const primaryMethod =
      recipient.payment_methods?.[0] ||
      null;

    const recipientChannel =
      primaryMethod?.channel ||
      recipient.default_channel ||
      "";

    const initialValues = {};

    if (primaryMethod?.recipient_fields) {
      Object.entries(
        primaryMethod.recipient_fields
      ).forEach(([key, value]) => {
        if (
          value === null ||
          value === undefined ||
          value === ""
        ) {
          return;
        }

        initialValues[key] = value;
      });
    }

    if (initialValues.mobile_number) {
      setMobileNumber(
        String(initialValues.mobile_number)
      );

      initialValues.mobile_number =
        String(
          initialValues.mobile_number
        );
    }

    setNickname(
      recipient.nickname || ""
    );

    const nameParts = (
      recipient.full_name || ""
    )
      .trim()
      .split(/\s+/);

    setFirstName(
      nameParts[0] || ""
    );

    setLastName(
      nameParts.slice(1).join(" ") ||
        ""
    );

    initCorridor(
      recipientCountry,
      recipientChannel,
      initialValues
    );

    setInitialized(true);
  }, [
    recipient,
    initialized,
    initCorridor,
  ]);

  useEffect(() => {
    if (!isMobileChannel || !initialized) {
      return;
    }

    const localNumber =
      getLocalMobileNumber(
        mobileNumber
      );

    if (
      corridorValues.mobile_number !==
      localNumber
    ) {
      setValue(
        "mobile_number",
        localNumber
      );
    }
  }, [
    mobileNumber,
    isMobileChannel,
    initialized,
    getLocalMobileNumber,
    corridorValues.mobile_number,
    setValue,
  ]);

  async function handleSubmit() {
    setSubmitError(null);

    if (!id || id === "undefined") {
      setSubmitError("Invalid recipient ID.");
      return;
    }

    if (!countryCode) {
      setSubmitError("Please select a country.");
      return;
    }

    if (!channelKey) {
      setSubmitError(
        "Please select how the recipient will receive funds."
      );
      return;
    }

    if (!fullName) {
      setSubmitError(
        "Please enter the recipient's name."
      );
      return;
    }

    if (!validate()) {
      setSubmitError(
        "Please fix the highlighted fields."
      );
      return;
    }

    const paymentMethod =
      buildPaymentMethodPayload();

    if (!paymentMethod) {
      setSubmitError(
        "Please fill out all required payment details."
      );
      return;
    }

    const recipientPayload = {
      nickname: nickname || fullName,
      full_name: fullName,
      country_code: countryCode,
      currency: paymentMethod.currency,
      default_channel: paymentMethod.channel,
      default_provider: paymentMethod.provider,
      status: "active",
    };

    setSubmitting(true);

    let recipientUpdated = false;

    try {
      await updateRecipientAsync({
        recipientId: id,
        ...recipientPayload,
      });

      recipientUpdated = true;

      await createPaymentMethodAsync({
        recipientId: id,
        ...paymentMethod,
        country_code: countryCode,
        is_default: true,
        status: "active",
      });

      navigate("/home");
    } catch (err) {
      if (recipientUpdated) {
        setSubmitError(
          safeApiErrorMessage(
            err,
            "Recipient details were saved, but the new payment method failed to save. Please try again.",
          ),
        );
      } else {
        setSubmitError(
          safeApiErrorMessage(
            err,
            "Something went wrong updating the recipient.",
          ),
        );
      }

    } finally {
      setSubmitting(false);
    }
  }

  if (isLoadingMe && !initialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFDFD]">
        <Loader2
          className="animate-spin text-[#0A4730]"
          size={32}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFDFD] pb-24 text-left">
      {/* MOBILE HEADER */}
      <div className="md:hidden bg-[#0A4730] rounded-b-[50px] px-6 pt-12 pb-8">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center active:scale-95 transition"
          >
            <ChevronLeft
              className="text-white"
              size={20}
            />
          </button>

          <div>
            <p className="text-[12px] text-white opacity-80 mb-1">
              Recipients
            </p>

            <h1 className="text-2xl text-[#C0F6B7] font-serif font-semibold leading-tight">
              Edit Recipient
            </h1>
          </div>
        </div>
      </div>

      {/* DESKTOP HEADER */}
      <div className="hidden md:flex items-center gap-3 max-w-xl mx-auto px-5 pt-10 pb-2">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="w-9 h-9 rounded-full border border-slate-200 bg-white flex items-center justify-center active:scale-95 transition text-[#0A4730]/60 hover:border-[#0A4730]/30"
        >
          <ChevronLeft size={18} />
        </button>

        <h1 className="text-2xl text-[#0A4730] font-serif font-semibold">
          Edit Recipient
        </h1>
      </div>

      {/* FORM */}
      <div className="max-w-xl mx-auto px-5 py-8 space-y-8">
        {/* Recipient Details */}
        <div className="space-y-2.5">
          <SectionLabel>
            Recipient Details
          </SectionLabel>

          <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-[0_4px_20px_-5px_rgba(0,0,0,0.05)] space-y-4">
            <div>
              <FieldLabel>
                Nickname (optional)
              </FieldLabel>

              <input
                type="text"
                value={nickname}
                onChange={(e) =>
                  setNickname(
                    e.target.value
                  )
                }
                placeholder="e.g. Mum"
                className={INPUT_CLASS}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <FieldLabel>
                  First Name
                </FieldLabel>

                <input
                  type="text"
                  value={firstName}
                  onChange={(e) =>
                    setFirstName(
                      e.target.value
                    )
                  }
                  placeholder="Martha"
                  className={INPUT_CLASS}
                />
              </div>

              <div>
                <FieldLabel>
                  Last Name
                </FieldLabel>

                <input
                  type="text"
                  value={lastName}
                  onChange={(e) =>
                    setLastName(
                      e.target.value
                    )
                  }
                  placeholder="Nderitu"
                  className={INPUT_CLASS}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Destination */}
        <div className="space-y-2.5">
          <SectionLabel>
            Destination
          </SectionLabel>

          <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-[0_4px_20px_-5px_rgba(0,0,0,0.05)]">
            <FieldLabel>
              Country / Region
            </FieldLabel>

            <Select
              options={countryOptions}
              styles={selectStyles}
              value={selectedCountry}
              onChange={(opt) => {
                setMobileNumber("");

                corridor.setCountryCode(
                  opt?.value || ""
                );
              }}
              isSearchable
              placeholder="Search country..."
              noOptionsMessage={() =>
                "No country found"
              }
            />
          </div>
        </div>

        {/* Payout Channel */}
        {countryCode && (
          <div className="space-y-2.5">
            <SectionLabel>
              Mode of Receiving
            </SectionLabel>

            <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-[0_4px_20px_-5px_rgba(0,0,0,0.05)]">
              {channelEntries.length === 0 ? (
                <p className="text-sm text-[#0A4730]/50">
                  No payout channels configured
                  for this country yet.
                </p>
              ) : (
                <div className="space-y-2.5">
                  {channelEntries.map(
                    ([key, channel]) => {
                      const Icon =
                        CHANNEL_ICONS[key] ||
                        Building2;

                      return (
                        <SelectionCard
                          key={key}
                          active={
                            channelKey === key
                          }
                          onClick={() => {
                            setMobileNumber("");

                            corridor.setChannelKey(
                              key
                            );
                          }}
                          icon={
                            <Icon size={18} />
                          }
                          label={channel.label}
                        />
                      );
                    }
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Dynamic Recipient Fields */}
        {activeChannel && (
          <div className="space-y-2.5">
            <SectionLabel>
              Payment Details
            </SectionLabel>

            <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-[0_4px_20px_-5px_rgba(0,0,0,0.05)] space-y-4">
              {recipientFields.map(
                (field) => {
                  if (
                    field.key ===
                      "mobile_number" &&
                    isMobileChannel
                  ) {
                    return (
                      <div key={field.key}>
                        <FieldLabel>
                          {field.label}
                        </FieldLabel>

                        <div className="flex gap-2">
                          <div className="flex items-center justify-center bg-white rounded-xl px-4 min-w-[76px] text-[#0A2540] font-semibold text-sm border border-slate-200">
                            {recipientDialCode ||
                              "+--"}
                          </div>

                          <input
                            type="text"
                            inputMode="numeric"
                            value={
                              displayMobileNumber
                            }
                            onChange={(e) => {
                              const digits =
                                e.target.value.replace(
                                  /\D/g,
                                  ""
                                );

                              setMobileNumber(
                                digits
                              );
                            }}
                            placeholder="712345678"
                            className={`${INPUT_CLASS} ${
                              corridorErrors[
                                field.key
                              ]
                                ? "!border-red-400 ring-2 ring-red-200 focus:!border-red-400 focus:ring-red-200"
                                : ""
                            }`}
                          />
                        </div>

                        {corridorErrors[
                          field.key
                        ] && (
                          <ErrorText>
                            {
                              corridorErrors[
                                field.key
                              ]
                            }
                          </ErrorText>
                        )}
                      </div>
                    );
                  }

                  return (
                    <DynamicField
                      key={field.key}
                      field={field}
                      value={
                        corridorValues[
                          field.key
                        ] || ""
                      }
                      onChange={(value) =>
                        setValue(
                          field.key,
                          value
                        )
                      }
                      errorMessage={
                        corridorErrors[
                          field.key
                        ]
                      }
                      banks={
                        field.type ===
                          "bank_select" &&
                        field.source?.startsWith(
                          "zuba_banks_"
                        )
                          ? banks
                          : undefined
                      }
                      banksLoading={
                        field.type ===
                          "bank_select" &&
                        field.source?.startsWith(
                          "zuba_banks_"
                        )
                          ? isLoadingBanks
                          : false
                      }
                    />
                  );
                }
              )}
            </div>
          </div>
        )}

        {/* Error */}
        {submitError && (
          <div className="rounded-2xl bg-red-50 border border-red-100 p-4 text-red-500 text-sm">
            {submitError}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3">
          <button
            type="button"
            disabled={submitting}
            onClick={handleSubmit}
            className="w-full h-14 rounded-2xl bg-[#0A4730] text-white font-bold tracking-wide hover:bg-[#0A4730]/90 transition-all duration-300 active:scale-[0.98] disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {submitting && (
              <Loader2
                size={16}
                className="animate-spin"
              />
            )}

            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}

const ErrorText = ({ children }) => (
  <p className="text-xs text-red-500 mt-1.5">
    {children}
  </p>
);

function DynamicField({
  field,
  value,
  onChange,
  errorMessage,
  banks,
  banksLoading,
}) {
  if (field.type === "hidden") {
    return null;
  }

  const hasError = Boolean(errorMessage);

  const inputClass = `${INPUT_CLASS} ${
    hasError
      ? "!border-red-400 ring-2 ring-red-200 focus:!border-red-400 focus:ring-red-200"
      : ""
  }`;

  if (field.type === "enum") {
    return (
      <div>
        <FieldLabel>
          {field.label}
        </FieldLabel>

        <Select
          options={(field.values || []).map(
            (value) => ({
              value,
              label: value,
            })
          )}
          styles={selectStyles}
          value={
            value
              ? {
                  value,
                  label: value,
                }
              : null
          }
          onChange={(option) =>
            onChange(
              option?.value || ""
            )
          }
          placeholder={`Select ${field.label.toLowerCase()}`}
          noOptionsMessage={() =>
            "No options"
          }
        />

        {hasError && (
          <ErrorText>
            {errorMessage}
          </ErrorText>
        )}
      </div>
    );
  }

  if (field.type === "bank_select") {
    if (banks !== undefined) {
      const options = (
        banks || []
      ).map((bank) => ({
        value: bank.bank_code,
        label: bank.name,
      }));

      const selected =
        options.find(
          (option) =>
            option.value === value
        ) || null;

      return (
        <div>
          <FieldLabel>
            {field.label}
          </FieldLabel>

          <Select
            options={options}
            styles={selectStyles}
            value={selected}
            onChange={(option) =>
              onChange(
                option?.value || ""
              )
            }
            isLoading={banksLoading}
            isSearchable
            placeholder="Select bank"
            noOptionsMessage={() =>
              banksLoading
                ? "Loading banks..."
                : "No banks found"
            }
          />

          {hasError && (
            <ErrorText>
              {errorMessage}
            </ErrorText>
          )}
        </div>
      );
    }

    return (
      <div>
        <FieldLabel>
          {field.label}
        </FieldLabel>

        <input
          type="text"
          value={value}
          onChange={(e) =>
            onChange(
              e.target.value
            )
          }
          placeholder={
            field.placeholder ||
            "Type bank name"
          }
          className={inputClass}
        />

        {hasError && (
          <ErrorText>
            {errorMessage}
          </ErrorText>
        )}
      </div>
    );
  }

  return (
    <div>
      <FieldLabel>
        {field.label}
        {field.optional
          ? " (optional)"
          : ""}
      </FieldLabel>

      <input
        type="text"
        value={value}
        onChange={(e) =>
          onChange(
            e.target.value
          )
        }
        placeholder={
          field.placeholder || ""
        }
        className={inputClass}
      />

      {hasError && (
        <ErrorText>
          {errorMessage}
        </ErrorText>
      )}
    </div>
  );
}

function SelectionCard({
  active,
  icon,
  label,
  onClick,
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full flex items-center justify-between px-5 py-4 rounded-2xl border transition duration-200 ${
        active
          ? "bg-[#0A4730] border-[#0A4730] text-white shadow-sm"
          : "bg-[#C0F6B7]/40 border-[#0A4730]/10 text-[#0A4730]"
      }`}
    >
      <div className="flex items-center gap-3">
        <span
          className={
            active
              ? "text-white"
              : "text-[#0A4730]/70"
          }
        >
          {icon}
        </span>

        <span className="text-sm font-medium">
          {label}
        </span>
      </div>

      {active ? (
        <CheckCircle2
          size={18}
          className="text-white"
        />
      ) : (
        <Circle
          size={18}
          className="text-[#0A4730]/20"
        />
      )}
    </button>
  );
}
